/**
 * RecycleBinPage — 回收站（docs/ui_page 规范）
 * 聚合系统中各类软删除的数据，支持按类型筛选和一键恢复。
 * 当前支持：接入应用（api_clients）；未来可扩展其他类型。
 */
import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Card,
  Combobox,
  Option,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogActions,
  InfoLabel,
} from '@fluentui/react-components'
import {
  SearchRegular,
  ArrowReset20Regular,
  Recycle20Regular,
} from '@fluentui/react-icons'
import { recycleApi, clientsApi, type RecycleItem } from '@/api/clients'
import { templatesApi } from '@/api/templates'
import { backupsApi } from '@/api/backups'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { PagedGrid, type PagedColumn } from '@/components/PagedGrid'
import { DialogCloseTitle } from '@/components/DialogCloseTitle'
import { formatDate } from '@/lib/format'

const PAGE_SIZE = 10

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '8px 12px',
  },
  queryCard: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  formRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    alignItems: 'flex-end',
  },
  keywordField: {
    flex: '2 0 240px',
    minWidth: '220px',
    flexShrink: 0,
  },
  typeField: {
    flex: '1 0 160px',
    minWidth: '140px',
  },
  actionField: {
    flex: '0 0 auto',
    display: 'flex',
    gap: '8px',
  },
  emptyHint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  typeBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
  },
  refText: {
    fontFamily: 'Consolas, "Courier New", monospace',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    wordBreak: 'break-all',
  },
})

export default function RecycleBinPage() {
  const styles = useStyles()
  const queryClient = useQueryClient()

  // ── 筛选状态 ──
  const [keyword, setKeyword] = useState('')
  const [selectedType, setSelectedType] = useState<string>('all')
  const [page, setPage] = useState(1)

  // ── 回收站列表 ──
  const { data: items = [], isLoading } = useQuery({
    queryKey: ['recycle-bin'],
    queryFn: recycleApi.list,
  })

  // ── 类型选项（从数据中动态提取，含「全部」） ──
  const typeOptions = useMemo(() => {
    const set = new Set<string>()
    items.forEach((i) => set.add(i.type))
    return [{ value: 'all', label: '全部类型' }, ...Array.from(set).map((t) => {
      const sample = items.find((i) => i.type === t)
      return { value: t, label: sample?.typeLabel ?? t }
    })]
  }, [items])

  // ── 过滤后的数据 ──
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (selectedType !== 'all' && item.type !== selectedType) return false
      if (keyword) {
        const kw = keyword.toLowerCase()
        return (
          item.name.toLowerCase().includes(kw) ||
          item.ref.toLowerCase().includes(kw) ||
          (item.domain ?? '').toLowerCase().includes(kw)
        )
      }
      return true
    })
  }, [items, selectedType, keyword])

  // ── 分页 ──
  const pagedItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE
    return filteredItems.slice(start, start + PAGE_SIZE)
  }, [filteredItems, page])

  // ── 恢复确认 ──
  const [restoreTarget, setRestoreTarget] = useState<RecycleItem | null>(null)
  const restoreMutation = useMutation({
    mutationFn: async () => {
      if (!restoreTarget) return
      // 按类型分发到对应的恢复接口
      if (restoreTarget.type === 'client') {
        await clientsApi.restore(Number(restoreTarget.id))
      } else if (restoreTarget.type === 'template') {
        await templatesApi.restore(Number(restoreTarget.id))
      } else if (restoreTarget.type === 'backup') {
        await backupsApi.restore(String(restoreTarget.id))
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recycle-bin'] })
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      queryClient.invalidateQueries({ queryKey: ['backups'] })
      toast.success('恢复成功', `${restoreTarget?.name} 已从回收站恢复`)
      setRestoreTarget(null)
    },
    onError: (err) => toast.error('恢复失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  // ── 表格列 ──
  const columns: PagedColumn<RecycleItem>[] = [
    {
      key: 'type',
      header: '类型',
      width: '100px',
      render: (item) => (
        <span className={styles.typeBadge}>{item.typeLabel}</span>
      ),
    },
    {
      key: 'name',
      header: '名称',
      width: '180px',
      render: (item) => <Text weight="semibold">{item.name}</Text>,
    },
    {
      key: 'domain',
      header: '所属域',
      width: '180px',
      render: (item) => item.domain ?? '-',
    },
    {
      key: 'ref',
      header: '标识',
      width: '220px',
      render: (item) => <span className={styles.refText}>{item.ref || '-'}</span>,
    },
    {
      key: 'deletedAt',
      header: '删除时间',
      width: '170px',
      render: (item) => formatDate(item.deletedAt),
    },
    {
      key: 'action',
      header: '操作',
      width: '100px',
      render: (item) => (
        <Button
          appearance="primary"
          size="small"
          icon={<ArrowReset20Regular />}
          onClick={(e) => {
            e.stopPropagation()
            setRestoreTarget(item)
          }}
        >
          恢复
        </Button>
      ),
    },
  ]

  return (
    <div className={styles.root}>
      {/* 查询面板 */}
      <Card className={styles.queryCard}>
        <div className={styles.formRow}>
          <div className={styles.keywordField}>
            <InfoLabel
              info="按名称、标识或所属域搜索"
              className={styles.keywordField}
            >
              关键词
            </InfoLabel>
            <Combobox
              placeholder="输入关键词搜索"
              value={keyword}
              onChange={(e) => setKeyword((e.target as HTMLInputElement).value)}
              freeform
              appearance="outline"
            >
              {keyword && (
                <Option value={keyword} text={`搜索「${keyword}」`}>
                  <SearchRegular style={{ marginRight: 8, verticalAlign: 'middle' }} />
                  搜索「{keyword}」
                </Option>
              )}
            </Combobox>
          </div>

          <div className={styles.typeField}>
            <InfoLabel info="按数据类型筛选">类型</InfoLabel>
            <Combobox
              value={typeOptions.find((o) => o.value === selectedType)?.label ?? '全部类型'}
              selectedOptions={[selectedType]}
              onOptionSelect={(_, data) => {
                if (data.optionValue) {
                  setSelectedType(data.optionValue)
                  setPage(1)
                }
              }}
              appearance="outline"
            >
              {typeOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Combobox>
          </div>

          <div className={styles.actionField}>
            <Button
              icon={<ArrowReset20Regular />}
              onClick={() => {
                setKeyword('')
                setSelectedType('all')
                setPage(1)
              }}
            >
              重置
            </Button>
          </div>
        </div>

        <div className={styles.emptyHint}>
          <Recycle20Regular style={{ verticalAlign: 'middle', marginRight: 4 }} />
          回收站集中存放系统中被软删除的数据，可在此查看和恢复。恢复后数据将回到原位置。
        </div>
      </Card>

      {/* 数据列表 */}
      <PagedGrid
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Recycle20Regular />
            回收站
          </span>
        }
        columns={columns}
        rows={pagedItems}
        rowKey={(item) => `${item.type}-${item.id}`}
        total={filteredItems.length}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        loading={isLoading}
        selectionMode="single"
        emptyText={items.length === 0 ? '回收站为空，暂无已删除的数据' : '没有匹配的记录'}
      />

      {/* 恢复确认 Dialog */}
      <Dialog
        open={!!restoreTarget}
        onOpenChange={(_, d) => {
          if (!d.open) setRestoreTarget(null)
        }}
        modalType="alert"
      >
        <DialogSurface>
          <DialogBody>
            <DialogCloseTitle title="确认恢复" onClose={() => setRestoreTarget(null)} />
            <DialogContent>
              <Text>
                确定要恢复 <Text weight="semibold">{restoreTarget?.name}</Text> 吗？
                <br />
                恢复后数据将回到「{restoreTarget?.typeLabel}」列表中，可正常使用。
              </Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setRestoreTarget(null)}>
                取消
              </Button>
              <Button
                appearance="primary"
                onClick={() => restoreMutation.mutate()}
                disabled={restoreMutation.isPending}
              >
                {restoreMutation.isPending ? '恢复中…' : '确认恢复'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}
